// src/components/staff/StaffSettingsTab.jsx
import { useState } from 'react';
import { useWmStaff } from '../../context/WmStaffContext';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';

const ROLE_LABELS = {
  senior:          { label: 'Senior',          color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
  support:         { label: 'Support',          color: '#2E8BF0', bg: 'rgba(46,139,240,0.15)' },
  account_manager: { label: 'Account Manager',  color: '#10B981', bg: 'rgba(16,185,129,0.15)' },
};

// ── Setup form — shown when no staff config is active ────────────────────────
function StaffSetupForm({ onActivate }) {
  const [form, setForm] = useState({ name: '', role: 'senior' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onActivate({ name: form.name.trim(), role: form.role });
  };

  return (
    <div className="p-6 max-w-md">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full bg-amber-400" style={{ boxShadow: '0 0 6px rgba(245,158,11,0.5)' }} />
          <span className="text-[var(--wm-text-caption)] font-bold uppercase tracking-widest text-amber-400">WrapMind Staff</span>
        </div>
        <h2 className="text-[var(--wm-text-body)] font-bold text-[#F8FAFE]">Activate Staff Access</h2>
        <p className="text-[var(--wm-text-ui)] text-[#7D93AE] mt-1 leading-relaxed">
          Set up your WrapMind staff identity on this device. Stored in localStorage — persists across sessions.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[var(--wm-text-ui)] font-medium text-[#7D93AE] mb-1.5">Your name</label>
          <input
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Tim"
            className="w-full h-9 px-3 text-[var(--wm-text-body)] rounded-lg border border-[#243348] bg-[#0F1923] text-[#F8FAFE] placeholder-[#4A6380] focus:outline-none focus:ring-2 focus:ring-amber-400/40"
            required
          />
        </div>
        <div>
          <label className="block text-[var(--wm-text-ui)] font-medium text-[#7D93AE] mb-1.5">Role</label>
          <select
            value={form.role}
            onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
            className="w-full h-9 px-3 text-[var(--wm-text-body)] rounded-lg border border-[#243348] bg-[#0F1923] text-[#F8FAFE] focus:outline-none focus:ring-2 focus:ring-amber-400/40"
          >
            <option value="senior">Senior — full access</option>
            <option value="support">Support — read-only views + internal tools</option>
            <option value="account_manager">Account Manager — assigned locations only</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={!form.name.trim()}
          className="w-full h-9 rounded-lg text-[var(--wm-text-body)] font-semibold bg-amber-400 text-[#0F1923] hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Activate
        </button>
      </form>
    </div>
  );
}

// ── Active config view ────────────────────────────────────────────────────────
function StaffActiveConfig({ wmStaffMember, onUpdate, onDeactivate }) {
  const { planTier, setPlanTier } = useFeatureFlags();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(wmStaffMember.name || '');
  const [editRole, setEditRole] = useState(wmStaffMember.role || 'senior');

  const roleStyle = ROLE_LABELS[wmStaffMember.role] ?? ROLE_LABELS.support;

  const handleSave = (e) => {
    e.preventDefault();
    if (!editName.trim()) return;
    onUpdate({ name: editName.trim(), role: editRole });
    setEditing(false);
  };

  return (
    <div className="p-6 max-w-lg space-y-5">
      {/* Identity card */}
      <div className="rounded-xl border border-amber-400/20 bg-amber-400/5 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400" style={{ boxShadow: '0 0 6px rgba(245,158,11,0.5)' }} />
            <span className="text-[var(--wm-text-caption)] font-bold uppercase tracking-widest text-amber-400">Active</span>
          </div>
          <button
            onClick={() => { setEditing(v => !v); setEditName(wmStaffMember.name || ''); setEditRole(wmStaffMember.role || 'senior'); }}
            className="text-[var(--wm-text-ui)] text-[#7D93AE] hover:text-[#F8FAFE] transition-colors"
          >
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </div>

        {editing ? (
          <form onSubmit={handleSave} className="space-y-3">
            <input
              value={editName}
              onChange={e => setEditName(e.target.value)}
              className="w-full h-9 px-3 text-[var(--wm-text-body)] rounded-lg border border-[#243348] bg-[#0F1923] text-[#F8FAFE] placeholder-[#4A6380] focus:outline-none focus:ring-2 focus:ring-amber-400/40"
              required
            />
            <select
              value={editRole}
              onChange={e => setEditRole(e.target.value)}
              className="w-full h-9 px-3 text-[var(--wm-text-body)] rounded-lg border border-[#243348] bg-[#0F1923] text-[#F8FAFE] focus:outline-none focus:ring-2 focus:ring-amber-400/40"
            >
              <option value="senior">Senior</option>
              <option value="support">Support</option>
              <option value="account_manager">Account Manager</option>
            </select>
            <button type="submit" disabled={!editName.trim()} className="h-8 px-4 rounded-lg text-[var(--wm-text-ui)] font-semibold bg-amber-400 text-[#0F1923] hover:bg-amber-300 disabled:opacity-40 transition-colors">
              Save
            </button>
          </form>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-400/20 border border-amber-400/30 flex items-center justify-center text-[var(--wm-text-body)] font-bold text-amber-300 flex-shrink-0">
              {(wmStaffMember.name || '?')[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-[var(--wm-text-body)] font-semibold text-[#F8FAFE]">{wmStaffMember.name}</p>
              <span style={{ background: roleStyle.bg, color: roleStyle.color, borderRadius: 4, padding: '1px 6px', fontSize: 'var(--wm-text-meta)', fontWeight: 600 }}>
                {roleStyle.label}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Plan tier override */}
      <div className="rounded-xl border border-[#243348] overflow-hidden">
        <div className="px-4 py-2.5 border-b border-[#243348]" style={{ background: 'rgba(255,255,255,0.02)' }}>
          <p className="text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#7D93AE]">Plan Tier Override</p>
        </div>
        <div className="p-4 space-y-2">
          <p className="text-[var(--wm-text-ui)] text-[#7D93AE] leading-relaxed">
            Force a specific pricing tier for testing gated features. Persists to localStorage.
          </p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'starter',      label: 'Starter',       desc: 'Basic only' },
              { id: 'professional', label: 'Professional',   desc: 'Standard paid' },
              { id: 'enterprise',   label: 'Enterprise',     desc: 'Multi-location' },
            ].map(tier => {
              const active = planTier === tier.id;
              return (
                <button
                  key={tier.id}
                  onClick={() => setPlanTier(tier.id)}
                  className={`relative flex flex-col items-start gap-0.5 rounded-lg px-3 py-2.5 text-left border transition-all ${
                    active
                      ? 'border-amber-500 bg-amber-400/10 ring-1 ring-amber-400/50'
                      : 'border-[#243348] bg-[#1B2A3E] hover:border-amber-400/30'
                  }`}
                >
                  {active && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-amber-400" />}
                  <span className={`text-[var(--wm-text-ui)] font-semibold ${active ? 'text-amber-300' : 'text-[#F8FAFE]'}`}>{tier.label}</span>
                  <span className={`text-[var(--wm-text-caption)] leading-tight ${active ? 'text-amber-400/80' : 'text-[#7D93AE]'}`}>{tier.desc}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Deactivate */}
      <div className="rounded-xl border border-red-500/20 p-4">
        <p className="text-[var(--wm-text-ui)] text-[#7D93AE] mb-3">Remove staff access from this device. You can re-activate at any time.</p>
        <button
          onClick={onDeactivate}
          className="px-4 py-1.5 text-[var(--wm-text-ui)] font-semibold rounded-lg bg-red-500/10 text-red-400 border border-red-500/25 hover:bg-red-500/20 transition-colors"
        >
          Deactivate staff access
        </button>
      </div>
    </div>
  );
}

// ── Public export ─────────────────────────────────────────────────────────────
export default function StaffSettingsTab() {
  const { wmStaffMember, activateStaff, deactivateStaff, updateStaffConfig, wmCan } = useWmStaff();

  if (!wmCan('internal.staff_manage') && wmStaffMember) {
    return (
      <div className="p-6 text-center text-[var(--wm-text-body)] text-[#7D93AE]">
        Senior access required to manage staff settings.
      </div>
    );
  }

  if (!wmStaffMember) {
    return <StaffSetupForm onActivate={activateStaff} />;
  }

  return (
    <StaffActiveConfig
      wmStaffMember={wmStaffMember}
      onUpdate={updateStaffConfig}
      onDeactivate={deactivateStaff}
    />
  );
}
